import { TicketInboxFilter } from "@jugl-web/rest-api/tickets/models/common-types/TicketInboxFilter";
import { ListBoxItem } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { ReactNode, useCallback, useMemo } from "react";
import { ReactComponent as AllIcon } from "../assets/all.svg";
import { ReactComponent as AssignedIcon } from "../assets/assigned.svg";
import { ReactComponent as InProgressIcon } from "../assets/in-progress.svg";
import { ReactComponent as IssuedIcon } from "../assets/issued.svg";
import { ReactComponent as OnCustomerIcon } from "../assets/on-customer.svg";
import { ReactComponent as OnHoldIcon } from "../assets/on-hold.svg";
import { ReactComponent as ResolvedIcon } from "../assets/resolved.svg";
import { ReactComponent as UnassignedIcon } from "../assets/unassigned.svg";

export type TicketsFilterOption = {
  id: TicketInboxFilter;
  label: string;
  icon: ReactNode;
};

export const useTicketsFilterOptions = () => {
  const { t } = useTranslations();

  const options = useMemo<TicketsFilterOption[]>(
    () => [
      {
        id: "all",
        label: t({ id: "common.all", defaultMessage: "All" }),
        icon: <AllIcon className="shrink-0" />,
      },
      {
        id: "assigned_to_user",
        label: t({
          id: "client-support-page.inbox-sidebar.assigned-to-me",
          defaultMessage: "Assigned to me",
        }),
        icon: <AssignedIcon className="shrink-0" />,
      },
      {
        id: "unassigned",
        label: t({
          id: "client-support-page.inbox-sidebar.unassigned",
          defaultMessage: "Unassigned",
        }),
        icon: <UnassignedIcon className="shrink-0" />,
      },
      {
        id: "in_progress",
        label: t({
          id: "client-support-page.inbox-sidebar.in-progress",
          defaultMessage: "In Progress",
        }),
        icon: <InProgressIcon className="shrink-0" />,
      },
      {
        id: "on_customer",
        label: t({
          id: "client-support-page.inbox-sidebar.on-customer",
          defaultMessage: "On Customer",
        }),
        icon: <OnCustomerIcon className="shrink-0" />,
      },
      {
        id: "on_hold",
        label: t({
          id: "client-support-page.inbox-sidebar.on-hold",
          defaultMessage: "On Hold",
        }),
        icon: <OnHoldIcon className="shrink-0" />,
      },
      {
        id: "issued",
        label: t({
          id: "client-support-page.inbox-sidebar.issued",
          defaultMessage: "Issued",
        }),
        icon: <IssuedIcon className="shrink-0" />,
      },
      {
        id: "resolved",
        label: t({
          id: "client-support-page.inbox-sidebar.resolved",
          defaultMessage: "Resolved",
        }),
        icon: <ResolvedIcon className="shrink-0" />,
      },
    ],
    [t]
  );

  const optionsById = useMemo(
    () =>
      Object.fromEntries(
        options.map((option) => [option.id, option])
      ) as Record<TicketInboxFilter, TicketsFilterOption>,
    [options]
  );

  const getFilterOptionById = useCallback(
    (filterId: TicketInboxFilter) => optionsById[filterId],
    [optionsById]
  );

  const listBoxOptions = useMemo<
    ListBoxItem<{ label: string; icon: ReactNode }>[]
  >(
    () =>
      options.map(({ id, label, icon }) => ({
        id,
        value: { label, icon },
      })),
    [options]
  );

  return {
    options,
    getFilterOptionById,
    listBoxOptions,
  };
};
